import { ViewStateStoreApi } from './viewState.store';
import { create } from 'zustand';
import type { SocketListener } from '../communication/websocket';
import { getWebsocketInterface } from '../communication/websocket';
import type { icModel } from 'generated-ts-common';
import type { at } from '../generated/Beans';
import { getCorrectNameForChatAuthor } from '../utils/utils';
type VirtualVisitChatMessage = icModel.saf.infrastructure.model.virtualvisit.VirtualVisitChatMessage;
type WebSocketOperation = at.sense.portlets.saf.virtualvisitpatient.model.WebSocketOperation;
import { PortletStoreApi } from '../App';
import { intl } from 'ehs-react';

export type ChatMessage = {
  id: number;
  sender: boolean;
  chatMessage: VirtualVisitChatMessage;
  isPatient: boolean;
};

export interface ChatStore {
  chatMessages: ChatMessage[];
  numberOfUnreadMessages: number;
  resetUnreadMessages: () => void;
  sendChatMessage: (msg: string) => void;
  loadChatNotificationCountFromSession: () => void;
  setChatMessages: (cmList: VirtualVisitChatMessage[]) => void;
  init: () => void;
  dispose: () => void;
}

let socketListener: SocketListener | undefined;

export const [useChatStore, ChatStoreApi] = create<ChatStore>((set, get) => ({
  chatMessages: [],
  numberOfUnreadMessages: 0,
  sendChatMessage: async (msg: string) => {
    if (msg) {
      console.log('send chat message', msg);
      const vsd = ViewStateStoreApi.getState().viewStateData;
      const mandatoryFieldForRegistration = PortletStoreApi.getState()
        .configuration.registrationConfig.mandatoryFieldForRegistration;
      const patientIdentifier =
        mandatoryFieldForRegistration === 'IDENTIFICATION'
          ? vsd?.enteredRegistrationData?.identificationNumber
          : vsd?.enteredRegistrationData?.familyName;
      if (vsd) {
        const isoDateTime = new Date().toISOString();
        const myMsg: any = {
          dateTimeIsoString: isoDateTime,
          chatMessageBody: msg,
          authorName: getCorrectNameForChatAuthor(vsd.patientData),
          authorId: patientIdentifier,
          authorType: 'PATIENT'
        };
        set(
          state =>
            (state.chatMessages = [
              ...state.chatMessages,
              {
                id: (state.chatMessages || []).length + 1,
                sender: true,
                chatMessage: myMsg,
                isPatient: true
              }
            ])
        );
        const socketData = {
          operation: 'SEND_CHAT_MESSAGE' as WebSocketOperation,
          chatMessage: myMsg
        };
        getWebsocketInterface().send(socketData);
      }
    }
  },
  loadChatNotificationCountFromSession: () => {
    console.log('loadChatNotificationCountFromSession');
    const notifStr = sessionStorage.getItem('chatNotificationCount');
    if (notifStr) {
      const notifObj = JSON.parse(notifStr);
      if (notifObj) {
        get().numberOfUnreadMessages = notifObj;
      }
    }
  },
  init: () => {
    socketListener = socketData => {
      if (socketData.callbackStatus === 'CHAT_MESSAGE_RECEIVED') {
        console.log(['@@@callBack_CHAT_MESSAGE_RECEIVED', socketData]);
        const myMsg = socketData.chatMessage;
        set(state =>
          state.chatMessages.push({
            id: (state.chatMessages || []).length + 1,
            sender: false,
            chatMessage: myMsg,
            isPatient: false
          })
        );
        set(
          state =>
            (state.numberOfUnreadMessages = state.numberOfUnreadMessages + 1)
        );
        sessionStorage.setItem(
          'chatNotificationCount',
          JSON.stringify(get().numberOfUnreadMessages)
        );
      }
    };
    getWebsocketInterface().addMessageListener(socketListener);
  },
  resetUnreadMessages: () => {
    console.log('resetUnreadMessages');
    set(state => (state.numberOfUnreadMessages = 0));
    sessionStorage.setItem(
      'chatNotificationCount',
      JSON.stringify(get().numberOfUnreadMessages)
    );
  },
  dispose: () => {
    if (socketListener) {
      getWebsocketInterface().removeMessageLisener(socketListener);
    }
  },
  setChatMessages: (cmList: VirtualVisitChatMessage[]) => {
    console.log('setChatMessages', cmList);
    let patName = '';
    const vsd = ViewStateStoreApi.getState().viewStateData;
    const patData = vsd?.patientData;
    if (patData) {
      patName = getCorrectNameForChatAuthor(patData);
    }
    const mandatoryFieldForRegistration = PortletStoreApi.getState()
      .configuration.registrationConfig.mandatoryFieldForRegistration;
    const patientIdentifier =
      mandatoryFieldForRegistration === 'IDENTIFICATION'
        ? vsd?.enteredRegistrationData?.identificationNumber
        : vsd?.enteredRegistrationData?.familyName;
    const cmMapped = cmList?.map((cm, index) => {
      cm.authorName =
        cm.authorName === '' || cm.authorName === undefined
          ? intl('common.unknown')
          : cm.authorName;
      return {
        id: index + 1,
        sender: cm.authorId === patientIdentifier,
        chatMessage: cm,
        isPatient: cm.authorName === patName
      } as ChatMessage;
    });
    set(state => (state.chatMessages = cmMapped));
  }
}));
